import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import api from '../../api/axios';
import { cleanBodyText, decodeHtmlEntities } from '../../utils/pressReleaseText';

interface DojRelease {
  id: string;
  title: string | null;
  url: string | null;
  publish_date: string | null;
  body_text: string | null;
}

// Extracted entity lists are stored as JSONB and shaped by schemebot extraction.
// Some entries are plain strings; others are objects like {name, type, evidence}.
// Render with the helper below — never assume one shape.
type ExtractedEntity = string | { name?: string; title?: string; agency?: string; type?: string; evidence?: string; [k: string]: any };

interface PressReleaseDetail {
  id: string;
  case_name: string;
  case_number: string | null;
  fraud_type: string;
  healthcare_subtype: string | null;
  settlement_usd: string | null;
  extracted_settlement_usd: string | null;
  settlement_evidence: string | null;
  alleged_loss_usd: string | null;
  alleged_loss_evidence: string | null;
  is_qui_tam: boolean;
  earliest_release_date: string | null;
  latest_release_date: string | null;
  final_posture: string | null;
  court_district: string | null;
  usao_district: string | null;
  state_code: string | null;
  fraud_start_year: number | null;
  fraud_end_year: number | null;
  fraud_period_evidence: string | null;
  defendants: ExtractedEntity[];
  relators: ExtractedEntity[];
  victim_agencies: ExtractedEntity[];
  victim_programs: ExtractedEntity[];
  investigators: ExtractedEntity[];
  government_attorneys: ExtractedEntity[];
  conduct_states: ExtractedEntity[];
  primary_release: DojRelease | null;
  release_count: number;
}

function entityLabel(e: ExtractedEntity): string {
  if (e === null || e === undefined) return '';
  if (typeof e === 'string') return e;
  if (typeof e === 'object') {
    return (
      e.name ||
      e.title ||
      e.agency ||
      e.display_name ||
      Object.values(e).find((v) => typeof v === 'string') ||
      JSON.stringify(e)
    );
  }
  return String(e);
}

function entityTooltip(e: ExtractedEntity): string | undefined {
  if (e && typeof e === 'object') {
    const parts: string[] = [];
    if (e.type) parts.push(`type: ${e.type}`);
    if (e.evidence) parts.push(`"${e.evidence}"`);
    return parts.length > 0 ? parts.join(' · ') : undefined;
  }
  return undefined;
}

const PressReleaseDetail = ({ pressReleaseId }: { pressReleaseId: string }) => {
  const [pr, setPr] = useState<PressReleaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPr(null);
    api
      .get(`/schemes/explore/press-releases/${pressReleaseId}/`)
      .then((res) => { if (!cancelled) setPr(res.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pressReleaseId]);

  if (loading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !pr) {
    return <Box sx={{ p: 4 }}><Typography color="error">{error || 'Press release not found'}</Typography></Box>;
  }

  const settlement = pr.settlement_usd ? parseFloat(pr.settlement_usd) : null;
  const allegedLoss = pr.alleged_loss_usd ? parseFloat(pr.alleged_loss_usd) : null;
  const fraudPeriod = [pr.fraud_start_year, pr.fraud_end_year].filter(Boolean).join('–');

  return (
    <Box>
      <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
              {decodeHtmlEntities(pr.case_name) || '(unnamed case)'}
            </Typography>
            {pr.case_number && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {pr.case_number}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              {pr.fraud_type && <Chip label={pr.fraud_type} size="small" variant="outlined" />}
              {pr.healthcare_subtype && <Chip label={pr.healthcare_subtype} size="small" variant="outlined" />}
              {pr.is_qui_tam && <Chip label="qui tam" size="small" color="primary" />}
              {pr.final_posture && <Chip label={pr.final_posture} size="small" />}
              {pr.state_code && <Chip label={pr.state_code} size="small" variant="outlined" />}
            </Box>
          </Box>
          {settlement !== null && (
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="overline" color="text.secondary">Settlement</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                ${settlement.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
              {allegedLoss !== null && allegedLoss !== settlement && (
                <Typography variant="caption" color="text.secondary">
                  alleged loss ${allegedLoss.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {pr.primary_release?.url && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              href={pr.primary_release.url}
              target="_blank"
              rel="noopener"
              sx={{ textTransform: 'none' }}
            >
              Open DOJ release
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
            gap: 3,
            // Long-text sections span the full grid via `data-span="all"`.
            '& [data-span="all"]': {
              gridColumn: { xs: 'auto', md: '1 / -1' },
            },
          }}
        >
          <Section title="Case facts">
            <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 0.5, columnGap: 2 }}>
              {pr.earliest_release_date && <>
                <Typography variant="body2" color="text.secondary">Earliest release</Typography>
                <Typography variant="body2">{pr.earliest_release_date}</Typography>
              </>}
              {pr.latest_release_date && pr.latest_release_date !== pr.earliest_release_date && <>
                <Typography variant="body2" color="text.secondary">Latest release</Typography>
                <Typography variant="body2">{pr.latest_release_date}</Typography>
              </>}
              {fraudPeriod && <>
                <Typography variant="body2" color="text.secondary">Fraud period</Typography>
                <Typography variant="body2">{fraudPeriod}</Typography>
              </>}
              {pr.court_district && <>
                <Typography variant="body2" color="text.secondary">Court district</Typography>
                <Typography variant="body2">{pr.court_district}</Typography>
              </>}
              {pr.usao_district && <>
                <Typography variant="body2" color="text.secondary">USAO</Typography>
                <Typography variant="body2">{pr.usao_district}</Typography>
              </>}
              {pr.conduct_states?.length > 0 && <>
                <Typography variant="body2" color="text.secondary">Conduct states</Typography>
                <Typography variant="body2">{pr.conduct_states.map(entityLabel).join(', ')}</Typography>
              </>}
              {pr.release_count > 1 && <>
                <Typography variant="body2" color="text.secondary">Releases linked</Typography>
                <Typography variant="body2">{pr.release_count}</Typography>
              </>}
            </Box>
          </Section>

          {pr.defendants?.length > 0 && (
            <Section title={`Defendants (${pr.defendants.length})`}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {pr.defendants.map((d, i) => (
                  <Chip key={i} label={entityLabel(d)} title={entityTooltip(d)} size="small" sx={{ mb: 0.5 }} />
                ))}
              </Box>
            </Section>
          )}

          {pr.relators?.length > 0 && (
            <Section title={`Relators (${pr.relators.length})`}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {pr.relators.map((r, i) => (
                  <Chip key={i} label={entityLabel(r)} title={entityTooltip(r)} size="small" color="primary" variant="outlined" sx={{ mb: 0.5 }} />
                ))}
              </Box>
            </Section>
          )}

          {pr.victim_agencies?.length > 0 && (
            <Section title="Defrauded agencies">
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {pr.victim_agencies.map((a, i) => (
                  <Chip key={i} label={entityLabel(a)} title={entityTooltip(a)} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                ))}
              </Box>
            </Section>
          )}

          {pr.victim_programs?.length > 0 && (
            <Section title="Funding programs">
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {pr.victim_programs.map((p, i) => (
                  <Chip key={i} label={entityLabel(p)} title={entityTooltip(p)} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                ))}
              </Box>
            </Section>
          )}

          {(pr.investigators?.length > 0 || pr.government_attorneys?.length > 0) && (
            <Section title="Government team">
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 0.5, columnGap: 2 }}>
                {pr.investigators?.length > 0 && <>
                  <Typography variant="body2" color="text.secondary">Investigators</Typography>
                  <Typography variant="body2">{pr.investigators.map(entityLabel).join(', ')}</Typography>
                </>}
                {pr.government_attorneys?.length > 0 && <>
                  <Typography variant="body2" color="text.secondary">Attorneys</Typography>
                  <Typography variant="body2">{pr.government_attorneys.map(entityLabel).join(', ')}</Typography>
                </>}
              </Box>
            </Section>
          )}

          {(pr.settlement_evidence || pr.alleged_loss_evidence || pr.fraud_period_evidence) && (
            <Section title="Extraction evidence" span="all">
              {pr.settlement_evidence && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Settlement</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>"{pr.settlement_evidence}"</Typography>
                </Paper>
              )}
              {pr.alleged_loss_evidence && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Alleged loss</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>"{pr.alleged_loss_evidence}"</Typography>
                </Paper>
              )}
              {pr.fraud_period_evidence && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Fraud period</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>"{pr.fraud_period_evidence}"</Typography>
                </Paper>
              )}
            </Section>
          )}

          {pr.primary_release?.body_text && (
            <Section title="DOJ release excerpt" span="all">
              <Paper variant="outlined" sx={{ minHeight: 600, maxHeight: 900, overflow: 'auto', backgroundColor: 'background.paper' }}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                    px: 2, pt: 2, pb: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {decodeHtmlEntities(pr.primary_release.title)}
                  </Typography>
                  {pr.primary_release.publish_date && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {pr.primary_release.publish_date}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontFamily: 'Georgia, serif' }}
                  >
                    {cleanBodyText(pr.primary_release.body_text)}
                  </Typography>
                </Box>
              </Paper>
            </Section>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Section = ({ title, children, span }: {
  title: string;
  children: React.ReactNode;
  /** `"all"` makes this section span every column in the parent grid. */
  span?: 'all';
}) => (
  <Box data-span={span}>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default PressReleaseDetail;
